import { useState, useCallback } from 'react';
import { ConfirmDialogVariant } from '../components/ui/ConfirmDialog';

interface ConfirmOptions {
  title: string;
  message: string;
  variant?: ConfirmDialogVariant;
  confirmText?: string;
  cancelText?: string;
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  onConfirm: () => void;
  isLoading: boolean;
}

export const useConfirm = () => {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: () => {},
    isLoading: false
  });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        ...options,
        onConfirm: () => {
          resolve(true);
          setState(prev => ({ ...prev, isOpen: false }));
        },
        isLoading: false
      });

      // Handle cancel
      const handleCancel = () => {
        resolve(false);
        setState(prev => ({ ...prev, isOpen: false }));
      };

      // Store cancel handler
      (setState as any).cancelHandler = handleCancel;
    });
  }, []);

  const close = useCallback(() => {
    if ((setState as any).cancelHandler) {
      (setState as any).cancelHandler();
    } else {
      setState(prev => ({ ...prev, isOpen: false }));
    }
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({ ...prev, isLoading }));
  }, []);

  return {
    confirm,
    close,
    setLoading,
    confirmState: state
  };
};

export default useConfirm;
